import React, { ChangeEvent, useEffect } from "react";

import { useCheckPasswordStrength, useGetReferrerByToken, useSearchParams, useTranslation } from "utils";

import { USER_TITLE } from "consts";

import { Checkbox, Flex, Form, FormInstance, Input, InputPassword, KlimRadioGroup, PasswordStrengthPopup, Typography } from "components";

import { ConfirmAccountFormType, PartnerOverrideConfiguration } from "pages/app/account/ConfirmAccount";

type FormElements = keyof Pick<ConfirmAccountFormType, "title" | "firstName" | "lastName" | "newsletter">;
type PartnerFormOverride = Partial<Record<FormElements, PartnerOverrideConfiguration>>;

/** Note that the partner name must EXACTLY match the name of the partner in the
 * `partners` table in the DB (as looked up by the referral code)
 */
const PARTNER_FORM_OVERRIDES_STEP_1: { [partnerName: string]: PartnerFormOverride | undefined } = {};

export const ConfirmAccountStep1 = ({ formRef }: { formRef: FormInstance<ConfirmAccountFormType> }) => {
  const { t } = useTranslation(["signinup", "meta", "enums"]);
  const { setPasswordMatch, setPasswordStrength, currentPasswordStrength } = useCheckPasswordStrength();

  const partnerReferralToken = useSearchParams()[0].get("partner");
  const referrerName = useGetReferrerByToken(partnerReferralToken ?? "");
  const partnerFormConfiguration = PARTNER_FORM_OVERRIDES_STEP_1[referrerName.data?.name?.toLowerCase() ?? ""];

  const handlePasswordChange = (e: ChangeEvent<HTMLInputElement>) => {
    setPasswordStrength(e);
  };

  const handleConfirmPasswordChange = (e: ChangeEvent<HTMLInputElement>) => {
    setPasswordMatch(e);
  };

  useEffect(() => {
    if (partnerFormConfiguration) {
      (Object.keys(partnerFormConfiguration) as FormElements[]).forEach((formElementName) =>
        partnerFormConfiguration?.[formElementName]?.value ? formRef.setFieldValue(formElementName, partnerFormConfiguration[formElementName].value) : undefined
      );
    }
  }, [formRef, partnerFormConfiguration]);

  return (
    <Flex vertical gap={32}>
      <Flex vertical gap={8}>
        <Typography variant="h2">{t("confirm.personalData")}</Typography>
        <Typography variant="paragraph-m">{t("confirm.personalDataDescription")}</Typography>
      </Flex>
      <Form.Item name="title" rules={[{ required: true }]}>
        <KlimRadioGroup
          className={`${partnerFormConfiguration?.title?.hidden ? "hidden" : ""}`}
          dataTestId="singup-title-label"
          value={partnerFormConfiguration?.title?.value ? partnerFormConfiguration?.title.value?.toString() : undefined}
          disabled={partnerFormConfiguration?.title?.value !== undefined}
          options={[
            { label: t(`confirm.inputs.title.${USER_TITLE.MR}`), value: USER_TITLE.MR },
            { label: t(`confirm.inputs.title.${USER_TITLE.MRS}`), value: USER_TITLE.MRS },
            { label: t(`confirm.inputs.title.${USER_TITLE.OTHER}`), value: USER_TITLE.OTHER },
          ]}
        />
      </Form.Item>
      <Input
        name="firstName"
        label={partnerFormConfiguration?.firstName?.label ?? t("confirm.inputs.firstName")}
        rules={[{ required: true }]}
        initialValue={partnerFormConfiguration?.firstName?.value ? partnerFormConfiguration?.firstName.value?.toString() : undefined}
        disabled={partnerFormConfiguration?.firstName?.value !== undefined}
      />
      <Input
        name="lastName"
        label={partnerFormConfiguration?.lastName?.label ?? t("confirm.inputs.lastName")}
        rules={[{ required: true }]}
        initialValue={partnerFormConfiguration?.lastName?.value ? partnerFormConfiguration?.lastName.value.toString() : undefined}
        disabled={partnerFormConfiguration?.lastName?.value !== undefined}
      />
      <InputPassword
        name="password"
        label={t("confirm.inputs.password")}
        rules={[{ required: true }]}
        PasswordStrengthPopup={<PasswordStrengthPopup currentPasswordStrength={currentPasswordStrength} />}
        onChange={(e: ChangeEvent<HTMLInputElement>) => handlePasswordChange(e)}
      />
      <InputPassword
        name="confirmPassword"
        label={t("confirm.inputs.confirmPassword")}
        rules={[{ required: true }]}
        PasswordStrengthPopup={<PasswordStrengthPopup currentPasswordStrength={currentPasswordStrength} />}
        onChange={(e: ChangeEvent<HTMLInputElement>) => handleConfirmPasswordChange(e)}
      />
      <Form.Item
        className={`${partnerFormConfiguration?.newsletter?.hidden ? "hidden" : ""}`}
        name="newsletter"
        valuePropName="checked"
        rules={[{ required: false }]}
        initialValue={typeof partnerFormConfiguration?.newsletter?.value === "boolean" ? partnerFormConfiguration?.newsletter.value : undefined}
      >
        <Checkbox>{t("confirm.inputs.newsletter")}</Checkbox>
      </Form.Item>
    </Flex>
  );
};
